import React, { useState, useRef, useEffect } from "react";
import Editor from "@monaco-editor/react";
import toast from "react-hot-toast";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion"; 
import { axiosInstance } from "../services/axios";
import { useAppStore } from "../zustand/useAppStore";

export const ComponentEditorPage = () => {
  const [components, setComponents] = useState([]);
  const [selectedComp, setSelectedComp] = useState(null);
  const [protectedRanges, setProtectedRanges] = useState([]);
  
  // 🌟 PER-COMPONENT MODELS STORE & ORIGINAL STRINGS MAP
  const modelsRef = useRef({});
  const originalStringsRef = useRef({});
  const protectedRangesStoreRef = useRef({});
  
  // 🌟 Force update trigger to refresh button states when typing
  const [, forceUpdate] = useState({});
  
  // 🌟 NEW STATES FOR CREATING A COMPONENT
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newCompId, setNewCompId] = useState("");
  const [newDescription, setNewDescription] = useState("");
  
  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isHeaderOpen, setIsHeaderOpen] = useState(true);

  const { themeMode } = useAppStore();
  const isDarkMode = themeMode === 'dark';

  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const decorationsRef = useRef([]);
  const isProgrammaticUpdate = useRef(false);
  const protectedRangesRef = useRef([]);
  const draftDecorationsRef = useRef([]);

  const rangeDebounceRef = useRef(null);

  useEffect(() => {
    fetchDynamicComponents();
  }, []);

  const fetchDynamicComponents = async () => {
    setIsFetching(true);
    try {
      const res = await axiosInstance.get('system-components.php');
      if (res.data.success) {
        const fetchedComps = res.data.components;
        setComponents(fetchedComps);
        fetchedComps.forEach(c => {
          originalStringsRef.current[c.comp_id] = c.comp_string || "";
        });
        return fetchedComps;
      }
    } catch (error) {
      console.error(error);
      toast.error("فشل في جلب المكونات الديناميكية");
    } finally {
      setIsFetching(false);
    }
  };

  const applyProtectedDecorations = (ranges) => {
    if (!editorRef.current || !monacoRef.current) return;
    
    const editor = editorRef.current;
    const monaco = monacoRef.current;

    const decorationObjects = ranges.map(range => ({
      range: new monaco.Range(range.start, 1, range.end, 1),
      options: {
        isWholeLine: true,
        className: 'bg-status-hoverBackground opacity-80 cursor-not-allowed',
      }
    }));

    decorationsRef.current = editor.deltaDecorations(decorationsRef.current, decorationObjects);
  };

  const applyDraftDecorations = (currentUIState) => {
    if (!editorRef.current || !monacoRef.current) return;
    
    const editor = editorRef.current;
    const monaco = monacoRef.current;

    const decorationObjects = [];

    currentUIState.forEach((draftRange, index) => {
      const originalRange = protectedRangesRef.current[index];

      if (originalRange) {
        if (originalRange.start !== draftRange.start || originalRange.end !== draftRange.end) {
          decorationObjects.push({
            range: new monaco.Range(draftRange.start, 1, draftRange.end, 1),
            options: {
              isWholeLine: true,
              className: 'bg-orange-500 opacity-60', 
            }
          });
        }
      } else {
        decorationObjects.push({
          range: new monaco.Range(draftRange.start, 1, draftRange.end, 1),
          options: {
            isWholeLine: true,
            className: 'bg-brand-primary/20 opacity-60', 
          }
        });
      }
    });

    draftDecorationsRef.current = editor.deltaDecorations(draftDecorationsRef.current, decorationObjects);
  };

  useEffect(() => {
    applyDraftDecorations(protectedRanges);
  }, [protectedRanges]);

  // 🌟 TRIGGER CREATE NEW COMPONENT WORKSPACE
  const handleStartNewComponent = () => {
    if (isCreatingNew) {
      setIsCreatingNew(false);
      setSelectedComp(null);
      if (editorRef.current && monacoRef.current) {
        const emptyModel = monacoRef.current.editor.createModel("", "javascript");
        editorRef.current.setModel(emptyModel);
      }
      setProtectedRanges([]);
      return;
    }

    setSelectedComp(null); 
    setIsCreatingNew(true);
    setNewCompId("");
    setNewDescription("");
    
    const defaultNewCode = "({ components, ...props }) => {\n  return (\n    <div className=\"w-full p-4\">\n      <h2>New Component</h2>\n    </div>\n  );\n};";
    
    if (editorRef.current && monacoRef.current) {
      const newModelKey = "new_component_draft";
      let model = modelsRef.current[newModelKey];
      if (!model) {
        model = monacoRef.current.editor.createModel(defaultNewCode, "javascript");
        modelsRef.current[newModelKey] = model;
      }
      editorRef.current.setModel(model);
    }
    
    setProtectedRanges([]);
    protectedRangesRef.current = [];

    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const handleSelectComponent = (comp) => {
    setIsCreatingNew(false); 
    isProgrammaticUpdate.current = true;
    setSelectedComp(comp);

    if (editorRef.current && monacoRef.current) {
      let model = modelsRef.current[comp.comp_id];
      if (!model) {
        model = monacoRef.current.editor.createModel(comp.comp_string || "", "javascript");
        modelsRef.current[comp.comp_id] = model;
      }
      editorRef.current.setModel(model);
    }

    let parsedRanges = [];
    try {
      const storedRanges = protectedRangesStoreRef.current[comp.comp_id];
      if (storedRanges) {
        parsedRanges = storedRanges;
      } else if (comp.protected_ranges) {
        parsedRanges = typeof comp.protected_ranges === 'string' 
          ? JSON.parse(comp.protected_ranges) 
          : comp.protected_ranges;
      }
    } catch (e) {
      console.error("Error parsing protected ranges", e);
      parsedRanges = [];
    }
    
    const finalRanges = Array.isArray(parsedRanges) ? parsedRanges : [];
    protectedRangesStoreRef.current[comp.comp_id] = finalRanges;
    
    setProtectedRanges(finalRanges.map(r => ({ ...r })));
    protectedRangesRef.current = finalRanges.map(r => ({ ...r }));

    setTimeout(() => {
      applyProtectedDecorations(finalRanges);
      isProgrammaticUpdate.current = false;
    }, 100);

    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    if (selectedComp) {
      let model = modelsRef.current[selectedComp.comp_id];
      if (!model) {
        model = monaco.editor.createModel(selectedComp.comp_string || "", "javascript");
        modelsRef.current[selectedComp.comp_id] = model;
      }
      editor.setModel(model);
    } else if (isCreatingNew) {
      let model = modelsRef.current["new_component_draft"];
      if (!model) {
        model = monaco.editor.createModel("({ components, ...props }) => {\n  return (\n    <div className=\"w-full p-4\">\n      <h2>New Component</h2>\n    </div>\n  );\n};", "javascript");
        modelsRef.current["new_component_draft"] = model;
      }
      editor.setModel(model);
    }

    applyProtectedDecorations(protectedRanges);
   
    editor.onDidChangeModelContent((event) => {
      if (isProgrammaticUpdate.current) return;

      const model = editor.getModel();
      if (!model) return;

      let hasViolation = false;
      
      for (const change of event.changes) {
        const startLine = change.range.startLineNumber;
        const endLine = change.range.endLineNumber;

        for (const range of protectedRangesRef.current) {
          if (
            (startLine >= range.start && startLine <= range.end) ||
            (endLine >= range.start && endLine <= range.end) ||
            (startLine <= range.start && endLine >= range.end)
          ) {
            hasViolation = true;
            break;
          }
        }
        if (hasViolation) break;
      }

      if (hasViolation) {
        isProgrammaticUpdate.current = true; 
        editor.trigger('keyboard', 'undo', null);
        
        toast.error("عذراً، هذا النطاق محمي ولا يمكن تعديله!", { 
          id: 'protected-range-error' 
        });
        
        setTimeout(() => {
          isProgrammaticUpdate.current = false;
        }, 10);
      } else {
        // Trigger re-render to update change state of button
        forceUpdate({});

        const liveRanges = decorationsRef.current.map(id => {
          const range = model.getDecorationRange(id);
          return range ? { start: range.startLineNumber, end: range.endLineNumber } : null;
        }).filter(Boolean);

        const rangesShifted = JSON.stringify(liveRanges) !== JSON.stringify(protectedRangesRef.current);
        if (rangesShifted) {
          protectedRangesRef.current = liveRanges.map(r => ({ ...r }));
          setProtectedRanges(liveRanges.map(r => ({ ...r }))); 
          if (selectedComp) {
            protectedRangesStoreRef.current[selectedComp.comp_id] = liveRanges.map(r => ({ ...r }));
          }
        }
      }
    });
  };

  const addProtectedRange = () => {
    const updated = [...protectedRanges, { start: 1, end: 10 }];
    setProtectedRanges(updated);
    if (selectedComp) {
      protectedRangesStoreRef.current[selectedComp.comp_id] = updated;
    }
  };

  const removeProtectedRange = (index) => {
    const updated = protectedRanges.filter((_, i) => i !== index);
    setProtectedRanges(updated);
    if (selectedComp) {
      protectedRangesStoreRef.current[selectedComp.comp_id] = updated;
    }
  };

  const updateRangeValue = (index, field, value) => {
    if (rangeDebounceRef.current) clearTimeout(rangeDebounceRef.current);

    const updated = protectedRanges.map(range => ({ ...range }));
    let parsedValue = parseInt(value);
    
    if (isNaN(parsedValue) || parsedValue < 1) {
      parsedValue = 1;
    }

    updated[index][field] = parsedValue;

    if (field === "start" && updated[index].end < parsedValue) {
      updated[index].end = parsedValue;
    } 
    
    setProtectedRanges(updated);

    if (selectedComp) {
      protectedRangesStoreRef.current[selectedComp.comp_id] = updated;
    }

    if (field === "end") {
      rangeDebounceRef.current = setTimeout(() => {
        setProtectedRanges((currentRanges) => {
          const latest = currentRanges.map(range => ({ ...range }));
          if (latest[index] && latest[index].start > latest[index].end) {
            latest[index].end = latest[index].start;
            return latest;
          }
          return currentRanges;
        });
      }, 800); 
    }
  };

  // 🌟 CHECK IF CURRENT MODEL HAS UN-SAVED CHANGES
  const hasChanges = () => {
    if (isCreatingNew) return true; // New component always can be saved
    if (!selectedComp || !editorRef.current) return false;
    const currentModel = editorRef.current.getModel();
    if (!currentModel) return false;
    const currentText = currentModel.getValue();
    const originalText = originalStringsRef.current[selectedComp.comp_id] || "";
    return currentText !== originalText;
  };

  // 🌟 SUPPORTS BOTH CREATE (POST) & UPDATE (PUT) WITH UNDO HISTORY PRESERVATION
  const handleSave = async () => {
    if (!editorRef.current) return;
    
    if (isCreatingNew && !newCompId.trim()) {
      toast.error("الرجاء إدخال معرف المكون (Comp ID)");
      return;
    }

    const fullContent = editorRef.current.getModel().getValue();
    setIsSaving(true);

    const validatedRanges = protectedRanges.map(range => ({
      start: range.start,
      end: range.end >= range.start ? range.end : range.start
    }));

    try {
      let response;
      const targetCompId = isCreatingNew ? newCompId.trim() : selectedComp.comp_id;

      if (isCreatingNew) {
        response = await axiosInstance.post('system-components.php', {
          comp_id: targetCompId,
          comp_string: fullContent,
          description: newDescription.trim(),
          is_active: 1,
          protected_ranges: JSON.stringify(validatedRanges)
        });
      } else {
        response = await axiosInstance.put('system-components.php', {
          comp_id: targetCompId,
          comp_string: fullContent,
          description: selectedComp.description,
          is_active: selectedComp.is_active,
          protected_ranges: JSON.stringify(validatedRanges)
        });
      }

      if (response.data && response.data.success) {
        toast.success(isCreatingNew ? "تم إنشاء المكون الجديد بنجاح!" : "تم تحديث المكون ونطاقات الحماية بنجاح!");
        
        // Update original baseline string cache (so the "unsaved changes" badge disappears)
        originalStringsRef.current[targetCompId] = fullContent;

        // 🌟 FIX: Do NOT dispose the Monaco model!
        // By keeping the model, we preserve the entire Ctrl+Z Undo history!
        if (isCreatingNew) {
          // If we created a new component, just rename its reference key from draft to the real ID
          if (modelsRef.current["new_component_draft"]) {
            modelsRef.current[targetCompId] = modelsRef.current["new_component_draft"];
            delete modelsRef.current["new_component_draft"];
          }
        }

        setIsCreatingNew(false);
        const freshComps = await fetchDynamicComponents(); 
        
        if (freshComps) {
          const freshSelectedComp = freshComps.find(c => c.comp_id === targetCompId);
          if (freshSelectedComp) {
            handleSelectComponent(freshSelectedComp);
          }
        }
        
        // Force re-render to instantly update the UI state
        forceUpdate({});

      } else {
        toast.error(response.data?.msg || "فشل حفظ العملية.");
      }
    } catch (error) {
      console.error(error);
      toast.error("خطأ في الاتصال بالخادم");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenSearch = () => {
    if (editorRef.current) {
      editorRef.current.focus();
      editorRef.current.trigger('keyboard', 'actions.find', null);
    }
  };

  const filteredComponents = components.filter(c => 
    c.comp_id.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (c.description && c.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const componentHasChanges = hasChanges();


  //if(true)return null;

  return (
    <div dir="rtl" className="relative flex w-full flex-1 h-full bg-bg-primary text-text-primary overflow-hidden">  
      {/* Mobile Backdrop */}
      {isSidebarOpen && (
        <div 
          onClick={() => setIsSidebarOpen(false)}
          className="absolute inset-0 bg-black/50 z-20 lg:hidden backdrop-blur-sm transition-opacity"
        />
      )}

      {/* SIDEBAR */}
      <div className={`absolute lg:relative inset-y-0 right-0 z-30 w-80 bg-bg-secondary border-l border-border flex flex-col h-full shadow-2xl lg:shadow-lg transition-all duration-300 ease-in-out ${
        isSidebarOpen || (!selectedComp && !isCreatingNew) ? "translate-x-0" : "translate-x-full lg:translate-x-0 lg:w-0 lg:overflow-hidden lg:border-l-0"
      }`}>
        <div className="p-5 border-b border-border">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-black text-brand-primary flex items-center gap-2">
              <Icon icon="solar:folder-open-bold-duotone" className="text-2xl" />
              المكونات الديناميكية
            </h2>
            
          <button 
            onClick={handleStartNewComponent}
            title={isCreatingNew ? "إلغاء الإنشاء" : "إضافة مكون جديد"}
            className={`px-3 py-1.5 ${isCreatingNew ? "bg-status-danger hover:opacity-80" : "bg-status-success hover:opacity-80"} text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1 shadow-sm`}
          >
            <Icon 
              icon={isCreatingNew ? "solar:close-circle-bold" : "solar:add-circle-bold-duotone"} 
              className="text-base" 
            /> 
            <span>{isCreatingNew ? "إلغاء" : "جديد"}</span>
          </button>
          </div>
          <div className="relative">
            <Icon icon="solar:magnifer-linear" className="absolute right-3.5 top-3 text-text-secondary text-base" />
            <input 
              type="text"
              placeholder="ابحث عن مكون..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-bg-primary border border-border rounded-xl pr-10 pl-4 py-2.5 text-sm outline-none focus:border-brand-primary transition-all"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3 custom-scrollbar">
          {isFetching ? (
            <div className="flex justify-center items-center h-32">
              <span className="animate-pulse font-bold text-brand-primary flex items-center gap-2">
                <Icon icon="solar:restart-bold" className="text-xl animate-spin" /> جاري التحميل...
              </span>
            </div>
          ) : filteredComponents.length > 0 ? (
            filteredComponents.map((comp) => {
              const compModel = modelsRef.current[comp.comp_id];
              const hasDraft = compModel && compModel.getValue() !== (originalStringsRef.current[comp.comp_id] || "");
              return (
                <div 
                  key={comp.comp_id}
                  onClick={() => handleSelectComponent(comp)}
                  className={`p-4 rounded-2xl cursor-pointer border transition-all duration-200 ${
                    selectedComp?.comp_id === comp.comp_id && !isCreatingNew
                      ? "bg-brand-primary/10 border-brand-primary shadow-sm" 
                      : "bg-bg-primary border-border hover:border-brand-primary/50 hover:shadow-sm"
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-sm text-text-primary break-all flex items-center gap-1.5">
                      <Icon icon="solar:code-file-bold-duotone" className="text-brand-primary text-base shrink-0" />
                      {comp.comp_id}
                    </h3>
                    <div className="flex items-center gap-1.5">
                      {hasDraft && (
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-black bg-amber-500/20 text-amber-500" title="لديك تعديلات غير محفوظة">
                          مسودة
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-black ${comp.is_active == 1 ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"}`}>
                        {comp.is_active == 1 ? "نشط" : "غير نشط"}
                      </span>
                    </div>
                  </div>
                  <p dir="auto" className="text-xs text-text-secondary line-clamp-1">
                    {comp.description || "لا يوجد وصف"}
                  </p>
                </div>
              );
            })
          ) : (
            <p className="text-center text-sm text-text-secondary mt-10">لا توجد مكونات مطابقة</p>
          )}
        </div>
      </div>

      {/* MAIN EDITOR WORKSPACE */}
      <div className="flex-1 flex flex-col h-full min-w-0 w-full bg-bg-primary relative">
        {(selectedComp || isCreatingNew) ? (
          <>
            <AnimatePresence>
              {!isHeaderOpen && (
                <motion.div
                  key="floating-expand"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  style={{translateX:'50%'}}
                  className="absolute top-2 right-1/2 z-20"
                >
                  <button
                    onClick={() => setIsHeaderOpen(true)}
                    title="إظهار لوحة التحكم"
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-bg-secondary/90 hover:bg-bg-secondary border border-brand-primary text-brand-primary rounded-full shadow-lg backdrop-blur-md text-xs font-bold"
                  >
                    <span>إظهار لوحة التحكم والتحرير</span>
                    <Icon icon="solar:alt-arrow-down-bold" className="text-sm" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence initial={false}>
              {isHeaderOpen && (
                <motion.div
                  key="editor-header"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="bg-bg-secondary border-b border-border shadow-sm overflow-hidden"
                >
                  <div className="flex flex-col py-4 px-6 gap-3">
                    <div className="flex items-center justify-between pt-1 md:pt-0">
                      <div className="w-1/2">
                        {isCreatingNew ? (
                          <div className="flex flex-col md:flex-row gap-2 w-full">
                            <input 
                              dir="auto"
                              type="text"
                              placeholder="معرف المكون (Comp ID)"
                              value={newCompId}
                              onChange={(e) => setNewCompId(e.target.value)}
                              className="p-2 bg-bg-primary border border-border rounded-xl text-sm font-bold outline-none focus:border-brand-primary"
                            />
                            <input 
                              dir="auto"
                              type="text"
                              placeholder="وصف المكون..."
                              value={newDescription}
                              onChange={(e) => setNewDescription(e.target.value)}
                              className="flex-1 p-2 bg-bg-primary border border-border rounded-xl text-sm outline-none focus:border-brand-primary"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2.5">
                            <h2 className="text-lg font-black text-text-primary">{selectedComp?.comp_id}</h2>
                            <span className="hidden md:flex px-2.5 py-0.5 bg-brand-primary/10 text-brand-primary rounded-lg text-xs font-bold items-center gap-1">
                              <Icon icon="solar:shield-check-bold-duotone" className="text-sm" /> محرر متقدم
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                          title="إظهار/إخفاء القائمة"
                          className="p-2.5 bg-bg-primary border border-border hover:border-brand-primary text-text-secondary hover:text-brand-primary rounded-xl transition-all shadow-sm flex items-center justify-center"
                        >
                          <Icon icon="solar:hamburger-menu-linear" className="text-lg" />
                        </button>

                        <button
                          onClick={() => setIsHeaderOpen(false)}
                          title="إخفاء لوحة التحكم"
                          className="p-2.5 bg-bg-primary border border-border hover:border-brand-primary text-text-secondary hover:text-brand-primary rounded-xl transition-all shadow-sm flex items-center justify-center"
                        >
                          <Icon icon="solar:minimize-square-3-bold-duotone" className="text-lg" />
                        </button>

                        <button
                          onClick={handleOpenSearch}
                          title="بحث داخل الكود"
                          className="p-2.5 bg-bg-primary border border-border hover:border-brand-primary text-text-secondary hover:text-brand-primary rounded-xl transition-all shadow-sm flex items-center justify-center"
                        >
                          <Icon icon="solar:magnifer-linear" className="text-lg" />
                        </button>

                        <button
                          onClick={handleSave}
                          disabled={isSaving || !componentHasChanges}
                          className={`px-2 md:px-6 py-2.5 font-bold rounded-xl transition-all shadow-lg flex items-center gap-2 justify-center ${
                            !componentHasChanges 
                              ? "bg-bg-tertiary text-text-secondary/40 border border-border cursor-not-allowed shadow-none" 
                              : isCreatingNew 
                                ? "bg-status-success hover:opacity-80 text-white" 
                                : "bg-brand-primary hover:bg-brand-hover text-white"
                          }`}
                        >
                          <Icon icon={isSaving ? "solar:restart-bold" : "solar:diskette-bold-duotone"} className={`text-lg ${isSaving ? "animate-spin" : ""}`} />
                          <span className="hidden md:block">{isSaving ? "جاري الحفظ..." : (isCreatingNew ? "إضافة المكون الجديد" : "حفظ التعديلات")}</span>
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 overflow-x-auto pt-2 border-t border-border pb-1">
                      <span className="text-xs shrink-0 font-bold text-text-secondary flex items-center gap-1">
                        <Icon icon="solar:lock-keyhole-bold-duotone" className="text-sm text-brand-primary" /> نطاقات الحماية:
                      </span>
                      {protectedRanges.map((range, index) => (
                        <div key={index} className="flex items-center gap-1.5 bg-bg-primary border border-border px-2.5 py-1 rounded-xl text-xs shrink-0">
                          <span>من</span>
                          <input 
                            type="number" 
                            min="1"
                            value={range.start} 
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => updateRangeValue(index, 'start', e.target.value)}
                            className="w-12 bg-bg-secondary border border-border text-center rounded px-1 text-text-primary font-bold outline-none" 
                          />
                          <span>إلى</span>
                          <input 
                            type="number"
                            min={range.start} 
                            value={range.end} 
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => updateRangeValue(index, 'end', e.target.value)}
                            className="w-12 bg-bg-secondary border border-border text-center rounded px-1 text-text-primary font-bold outline-none" 
                          />
                          <button 
                            onClick={() => removeProtectedRange(index)}
                            className="text-red-500 hover:text-red-700 font-bold px-1 ml-1"
                          >
                            <Icon icon="solar:close-circle-bold" className="text-sm" />
                          </button>
                        </div>
                      ))}
                      <button 
                        onClick={addProtectedRange}
                        className="px-3 shrink-0 py-1 bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                      >
                        <Icon icon="solar:add-circle-bold-duotone" className="text-sm" /> إضافة نطاق محمي
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Monaco Editor Workspace */}
           <div className="flex-1 relative w-full h-full min-h-[400px]" dir="ltr">
              <Editor
                height="100%"
                width="100%"
                language="javascript"
                theme={isDarkMode ? "vs-dark" : "light"}
                onMount={handleEditorMount}
                options={{
                  minimap: { enabled: true, scale: 0.75 },
                  fontSize: 15,
                  fontFamily: "'Fira Code', 'Consolas', monospace",
                  fontLigatures: true,
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  wordWrap: "on",
                  tabSize: 2,
                  smoothScrolling: true,
                  cursorBlinking: "smooth",
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-text-secondary h-full">
            <Icon icon="solar:code-square-bold-duotone" className="text-6xl mb-3 opacity-30 text-brand-primary" />
            <h2 className="text-xl font-black mb-1 text-text-primary">مساحة العمل فارغة</h2>
            <p className="text-xs">يرجى اختيار مكون ديناميكي من القائمة الجانبية أو إنشاء مكون جديد للبدء</p>
          </div>
        )}
      </div>

    </div>
  );
};